import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FaCogs, FaClipboardList, FaRegLightbulb, FaExchangeAlt, FaQuestionCircle, FaPlus, FaMinus, FaLinkedin, FaFacebookF } from 'react-icons/fa';
import { MdDataUsage, MdOutlineStrategy, MdPeopleOutline, MdCheck } from 'react-icons/md';

// ----------------------------------------------------------------------
// 1. Global Styles
// ----------------------------------------------------------------------

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    font-family: 'Inter', sans-serif;
    color: #2d3748;
    background-color: #fff;
    line-height: 1.7;
    scroll-behavior: smooth;
  }

  html {
    scroll-behavior: smooth;
  }
`;

// ----------------------------------------------------------------------
// 2. Main Containers and Layout
// ----------------------------------------------------------------------

const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Section = styled.section`
  padding: 80px 0;
  background-color: ${(props) => props.$bgColor || 'transparent'};
  color: ${(props) => props.$color || '#2d3748'};
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

// ----------------------------------------------------------------------
// 3. Unlocking LinkedIn Section (Top)
// ----------------------------------------------------------------------

const LinkedInHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const LinkedInTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: #1a365d;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const LinkedInSubtitle = styled.p`
  font-size: 18px;
  color: #718096;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.8;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 30px 0;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 30px;
  padding: 30px;
  border-radius: 16px;
  transition: all 0.3s ease;
  background: ${props => props.$isEven ? '#f8fafc' : 'transparent'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    padding: 20px;
  }
`;

const StepNumberCircle = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  background: linear-gradient(135deg, #3182ce 0%, #2b6cb0 100%);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  font-weight: 700;
  color: white;
  box-shadow: 0 4px 12px rgba(49, 130, 206, 0.3);
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    min-width: 50px;
    font-size: 20px;
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const StepDescription = styled.p`
  font-size: 16px;
  color: #718096;
  line-height: 1.8;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

// ----------------------------------------------------------------------
// 4. Why Choose Us Section (Middle)
// ----------------------------------------------------------------------

const WhyChooseUsSection = styled(Section)`
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const ChooseUsTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 80px;
  position: relative;
  
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 60px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-bottom: 60px;
  position: relative;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  padding: 40px 30px;
  border-radius: 20px;
  text-align: center;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }
`;

const CardIcon = styled.div`
  font-size: 48px;
  color: #f6ad55;
  margin-bottom: 25px;
  position: relative;
  z-index: 2;
`;

const CardTitle = styled.h4`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #fff;
  position: relative;
  z-index: 2;
`;

const CardDescription = styled.p`
  font-size: 16px;
  color: #e2e8f0;
  line-height: 1.7;
  position: relative;
  z-index: 2;
`;

const SubCardGrid = styled(CardGrid)`
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 0;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SubCard = styled(Card)`
  background: rgba(0, 0, 0, 0.2);
  padding: 30px 25px;
  min-height: 280px;
  
  ${CardIcon} {
    font-size: 36px;
    margin-bottom: 20px;
  }
  
  ${CardTitle} {
    font-size: 18px;
  }

  ${CardDescription} {
    font-size: 15px;
  }
`;

// ----------------------------------------------------------------------
// 5. FAQ Section (Bottom)
// ----------------------------------------------------------------------

const FAQSection = styled(Section)`
  background: #f8fafc;
`;

const FAQTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 60px;
  color: #1a365d;
  
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 40px;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  max-width: 900px;
  margin: 0 auto;
`;

const FAQWrapper = styled.div`
  background: white;
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FAQItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  padding: 25px 30px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f7fafc;
  }
`;

const FAQQuestion = styled.p`
  font-size: 18px;
  color: #2d3748;
  display: flex;
  align-items: center;
  font-weight: 600;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const FAQIcon = styled.span`
  margin-right: 15px;
  color: #3182ce;
  font-size: 20px;
  min-width: 20px;
`;

const FAQToggleIcon = styled.span`
  font-size: 20px;
  color: #718096;
  transition: all 0.3s ease;
  transform: rotate(${props => props.$isOpen ? '45deg' : '0'});
`;

const FAQAnswer = styled.div`
  font-size: 16px;
  color: #4a5568;
  line-height: 1.7;
  padding: ${props => props.$isOpen ? '0 30px 25px 65px' : '0 30px 0 65px'};
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.4s ease-in-out;
  
  @media (max-width: 768px) {
    padding: ${props => props.$isOpen ? '0 20px 20px 55px' : '0 20px 0 55px'};
    font-size: 15px;
  }
`;

// ----------------------------------------------------------------------
// 6. Footer (Bottom Bar)
// ----------------------------------------------------------------------

const FooterBar = styled.div`
  background: linear-gradient(135deg, #1a365d 0%, #2d3748 100%);
  color: #fff;
  padding: 50px 0;
  text-align: center;
`;

const FollowUsText = styled.p`
  font-size: 16px;
  margin-bottom: 20px;
  font-weight: 500;
  opacity: 0.9;
`;

const SocialLinksText = styled.p`
  font-size: 18px;
  margin-bottom: 30px;
  opacity: 0.8;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const SocialButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 30px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  color: #fff;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

// ----------------------------------------------------------------------
// 7. Data (Updated with Enhanced Answers)
// ----------------------------------------------------------------------

const linkedinSteps = [
    { 
        number: 1, 
        title: "Profile Power-Up", 
        description: "Transform your LinkedIn profile into a powerful marketing asset. We optimize every section with strategic keywords, compelling storytelling, and professional branding that showcases your unique value proposition. A well-crafted profile increases visibility by up to 300% and establishes immediate credibility with potential clients and partners." 
    },
    { 
        number: 2, 
        title: "Engaging Content Strategy", 
        description: "Develop a data-driven content calendar that positions you as an industry thought leader. We create a mix of educational articles, engaging visual content, and interactive posts that drive meaningful conversations. Our approach increases engagement rates by 400% while building a loyal community around your brand." 
    },
    { 
        number: 3, 
        title: "Strategic Networking", 
        description: "Go beyond random connections with our targeted networking methodology. We identify and engage with key decision-makers, industry influencers, and potential partners through personalized outreach. Our systematic approach helps build quality relationships that convert into valuable business opportunities and partnerships." 
    },
    { 
        number: 4, 
        title: "Thought Leadership Development", 
        description: "Establish your authority in the industry through consistent value-driven content. We help you share original insights, comment on industry trends, and participate in relevant discussions. This positions you as a go-to expert, attracting high-quality leads and partnership opportunities organically." 
    },
    { 
        number: 5, 
        title: "Targeted Outreach Campaigns", 
        description: "Execute precision-targeted outreach campaigns that deliver results. We craft personalized connection requests and follow-up sequences that resonate with your ideal audience. Our methodology achieves response rates 5x higher than generic templates while maintaining authentic relationship-building." 
    },
    { 
        number: 6, 
        title: "Performance Analytics & Optimization", 
        description: "Continuously measure and refine your strategy using advanced analytics. We track key metrics including engagement rates, lead generation, and conversion data to optimize your approach. Our data-driven adjustments ensure maximum ROI and sustainable long-term growth on the platform." 
    },
];

const mainCards = [
    { 
        icon: FaCogs, 
        title: "Cutting-Edge Technology", 
        description: "Leverage our advanced AI-powered tools and analytics platforms that provide real-time insights and automation capabilities. Our technology stack integrates seamlessly with LinkedIn's API to deliver precision targeting, automated engagement tracking, and predictive analytics for optimal campaign performance." 
    },
    { 
        icon: MdPeopleOutline, 
        title: "Tailored Strategy Development", 
        description: "Receive a completely customized LinkedIn strategy designed specifically for your business objectives. We conduct in-depth analysis of your target audience, competitive landscape, and industry trends to create a roadmap that delivers measurable results and sustainable growth." 
    },
    { 
        icon: FaClipboardList, 
        title: "Expert Campaign Management", 
        description: "Benefit from our team of certified LinkedIn marketing specialists who manage every aspect of your campaign. From content creation and community management to paid advertising and analytics, we provide end-to-end management that maximizes your ROI and saves you valuable time." 
    },
];

const subCards = [
    { 
        icon: MdDataUsage, 
        title: "Data-Driven Decision Making", 
        description: "Every strategy decision is backed by comprehensive data analysis and A/B testing. We monitor 50+ key performance indicators to continuously optimize your campaigns, ensuring every marketing dollar is invested where it delivers the highest return." 
    },
    { 
        icon: FaRegLightbulb, 
        title: "Integrated Marketing Approach", 
        description: "We seamlessly integrate your LinkedIn strategy with your overall marketing ecosystem. Our holistic approach ensures consistent messaging across channels while leveraging LinkedIn's unique capabilities to amplify your broader marketing objectives." 
    },
    { 
        icon: FaExchangeAlt, 
        title: "Transparent Collaboration", 
        description: "Experience true partnership with regular strategy reviews, detailed performance reporting, and direct access to your dedicated account team. We believe in complete transparency and work collaboratively to achieve your business goals." 
    },
];

const faqData = [
    { 
        question: "What is LinkedIn Marketing, and why is it important for businesses?",
        answer: "LinkedIn Marketing is the strategic use of LinkedIn's professional network to achieve business objectives including brand awareness, lead generation, talent acquisition, and thought leadership positioning. It's critically important because LinkedIn offers direct access to over 900 million professionals, including 65 million decision-makers and 10 million C-level executives. Unlike other social platforms, LinkedIn users are in a professional mindset, making it ideal for B2B marketing with conversion rates that are 3x higher than other channels."
    },
    { 
        question: "How can I measure the success of my LinkedIn Marketing efforts?",
        answer: "We measure success through a comprehensive dashboard tracking multiple KPIs: Engagement Metrics (likes, comments, shares, click-through rates), Audience Growth (follower acquisition rate, connection growth), Lead Generation (form submissions, qualified leads), and Business Impact (website conversions, sales revenue attribution). Our clients receive monthly performance reports with clear ROI calculations and actionable insights for continuous improvement. Typically, we see clients achieve 200-400% increase in lead quality within the first 3 months."
    },
    { 
        question: "How can SRK Software help me with my LinkedIn Marketing?",
        answer: "SRK Software provides end-to-end LinkedIn marketing solutions including: Strategic profile optimization, content strategy development, daily community management, targeted advertising campaigns, influencer partnership programs, and comprehensive analytics reporting. Our team of specialists becomes an extension of your marketing department, handling everything from strategy to execution while providing regular performance reviews and strategic adjustments. Clients typically see a 5x return on investment within the first 6 months of partnership."
    },
    { 
        question: "Do I need a LinkedIn Company page to engage in LinkedIn Marketing?",
        answer: "Yes, a Company Page is essential for professional LinkedIn marketing. While personal profiles are great for individual networking, Company Pages provide crucial features including: Advanced analytics, employee advocacy tools, showcase pages for different business units, sponsored content capabilities, and career page integration. We help clients optimize both personal profiles and Company Pages to create a powerful synergistic presence that maximizes reach and credibility."
    },
    { 
        question: "What types of content work best for LinkedIn Marketing?",
        answer: "High-performing content on LinkedIn includes: Long-form articles demonstrating expertise (3-5 minute read time), data-driven insights and original research, professional video content (under 3 minutes), carousel posts with actionable tips, and thought-provoking questions that spark discussion. We've found that content providing genuine educational value outperforms promotional content by 300%. Our content strategy focuses on establishing authority while driving meaningful engagement."
    },
    { 
        question: "Is LinkedIn Marketing suitable for all industries and business sizes?",
        answer: "Absolutely. While traditionally strong for B2B, we've successfully implemented LinkedIn strategies for B2C companies, non-profits, educational institutions, and professional services. The platform's sophisticated targeting allows precision reach regardless of industry. For small businesses, we focus on cost-effective relationship building and local authority establishment. For enterprises, we implement scaled programs with multiple decision-maker touchpoints across different departments."
    },
    { 
        question: "How often should I update my LinkedIn profile for marketing purposes?",
        answer: "We recommend quarterly strategic reviews and monthly content updates. Profile optimization should occur: During initial setup, when changing roles/companies, when targeting new markets, and quarterly for performance optimization. For active marketing, we suggest posting valuable content 3-5 times weekly and engaging with your network daily. Our managed service includes continuous optimization based on performance data and algorithm changes."
    },
    { 
        question: "Can LinkedIn Marketing be integrated with other marketing channels?",
        answer: "Yes, integration is crucial for maximum impact. We seamlessly connect LinkedIn with your: CRM system for lead tracking, email marketing platforms for nurture sequences, website analytics for conversion tracking, and other social channels for consistent messaging. Our integrated approach ensures LinkedIn efforts amplify your overall marketing strategy, with typical clients seeing a 35% increase in marketing efficiency across all channels."
    },
];

// ----------------------------------------------------------------------
// 8. The Component
// ----------------------------------------------------------------------

const LinkedInPage2 = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <>
            <GlobalStyle />
            
            {/* 4. Footer */}
            <FooterBar>
                <PageContainer>
                    <FollowUsText>Connect With Us</FollowUsText>
                    <SocialLinksText>
                        Stay updated with the latest LinkedIn marketing insights, industry trends, and success stories. 
                        Join our community of growth-focused professionals.
                    </SocialLinksText>
                    <ButtonContainer>
                        <SocialButton href="#linkedin">
                            <FaLinkedin /> Connect on LinkedIn
                        </SocialButton>
                        <SocialButton href="#facebook">
                            <FaFacebookF /> Follow on Facebook
                        </SocialButton>
                    </ButtonContainer>
                </PageContainer>
            </FooterBar>
        </>
    );
};

export default LinkedInPage2;